import { z } from 'zod';

/**
 * Runtime schemas for everything stored in Firestore.
 *
 * Firestore has no schema of its own, so until now the shape of a pun or a user
 * existed only in the heads of whoever wrote the components. These schemas make
 * it explicit and checkable.
 *
 * These are *read* schemas and are deliberately lenient: existing documents were
 * written by loosely-typed code over many months, so missing and malformed
 * fields are expected. They coerce and default rather than throw, so one bad
 * document cannot blank out a feed.
 *
 * Write paths need the opposite — strict validation that rejects bad input.
 * Those get added when writes move server-side (roadmap step 5), at which point
 * this module is shared with functions/ rather than living only in the client.
 */

/* -------------------------------------------------------------------------- */
/* Timestamps                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Firestore hands back timestamps in several shapes depending on whether the
 * value came from the server, from cache, or from an optimistic local write:
 * a Timestamp instance, a plain `{seconds, nanoseconds}` object, a Date, or an
 * ISO string. This normalises all of them to a Date.
 *
 * Replaces the `getDateFromTimestamp` helper that was duplicated across
 * components.
 */
export const firestoreDate = z.preprocess((value): Date | undefined => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  if (typeof value === 'object' && value !== null) {
    const candidate = value as { toDate?: () => Date; seconds?: number };

    if (typeof candidate.toDate === 'function') {
      const date = candidate.toDate();
      return date instanceof Date && !Number.isNaN(date.getTime()) ? date : undefined;
    }

    if (typeof candidate.seconds === 'number') {
      return new Date(candidate.seconds * 1000);
    }
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  return undefined;
}, z.date());

/* -------------------------------------------------------------------------- */
/* Reactions                                                                   */
/* -------------------------------------------------------------------------- */

export const REACTIONS = ['crown', 'laugher', 'thinker', 'banger'] as const;

export const reactionSchema = z.enum(REACTIONS);
export type Reaction = z.infer<typeof reactionSchema>;

/** Display metadata for each reaction, in the order they are rendered. */
export const REACTION_META: ReadonlyArray<{
  name: Reaction;
  emoji: string;
  label: string;
  /** The crown is limited to one per user per day. */
  special: boolean;
}> = [
  { name: 'crown', emoji: '👑', label: 'Crown (1 per day)', special: true },
  { name: 'laugher', emoji: '😂', label: 'Laugher', special: false },
  { name: 'thinker', emoji: '🤔', label: 'Thinker', special: false },
  { name: 'banger', emoji: '🔥', label: 'Banger', special: false },
];

/**
 * `reactions` maps a user id to that user's reactions. In practice the array
 * holds zero or one entries — reacting again replaces the previous reaction.
 * Unknown reaction names in old documents are dropped rather than rejected.
 */
export const reactionsMapSchema = z
  .record(z.string(), z.array(z.unknown()).catch([]))
  .catch({})
  .transform((raw): Record<string, Reaction[]> => {
    const result: Record<string, Reaction[]> = {};
    for (const [uid, list] of Object.entries(raw)) {
      const valid = list.filter(
        (entry): entry is Reaction => reactionSchema.safeParse(entry).success,
      );
      if (valid.length > 0) result[uid] = valid;
    }
    return result;
  });

/* -------------------------------------------------------------------------- */
/* Puns                                                                        */
/* -------------------------------------------------------------------------- */

export const punSchema = z.object({
  id: z.string(),
  text: z.string().catch(''),
  userId: z.string(),
  category: z.string().catch(''),
  output: z.string().catch(''),
  createdAt: firestoreDate.catch(() => new Date()),
  /** Pacific-time calendar day, `YYYY-MM-DD`. Used to enforce one pun per day. */
  pstDay: z.string().optional(),
  reactions: reactionsMapSchema,
});

export type Pun = z.infer<typeof punSchema>;

/* -------------------------------------------------------------------------- */
/* Users                                                                       */
/* -------------------------------------------------------------------------- */

/** Minutes past local midnight. 18:00 keeps the behaviour of the old fixed cron. */
export const DEFAULT_REMINDER_MINUTE = 18 * 60;

/** Minutes past local midnight. 8am, so it lands with the rest of the morning. */
export const DEFAULT_MORNING_NOTIFICATION_MINUTE = 8 * 60;

export const userSettingsSchema = z
  .object({
    displayName: z.string().optional(),
    /** Lowercased copy of displayName, used for prefix search. */
    displayNameLower: z.string().optional(),
    /** The daily "you have not posted yet" reminder. */
    notificationsEnabled: z.boolean().catch(false),
    /** When someone you follow posts. */
    friendNotificationsEnabled: z.boolean().catch(false),
    /**
     * When someone crowns one of your puns. Only crowns: the other three
     * reactions are common enough that notifying on them is noise.
     */
    crownNotificationsEnabled: z.boolean().catch(false),
    /**
     * Minutes past midnight *in the user's own timezone*, so "8pm" means 8pm
     * where they are rather than in Pacific.
     */
    reminderMinuteOfDay: z.number().int().min(0).max(1439).catch(DEFAULT_REMINDER_MINUTE),
    /**
     * The other daily notification: today's prompt, sent once it exists,
     * whether or not you have posted yet. Independent of `notificationsEnabled` —
     * one is "here is today's prompt", the other is "you haven't answered it".
     */
    morningNotificationEnabled: z.boolean().catch(false),
    /** Same units as `reminderMinuteOfDay`, in the user's own timezone. */
    morningNotificationMinuteOfDay: z
      .number()
      .int()
      .min(0)
      .max(1439)
      .catch(DEFAULT_MORNING_NOTIFICATION_MINUTE),
    /** IANA zone both daily notifications are interpreted in, e.g. 'Europe/London'. */
    timeZone: z.string().optional(),
    /**
     * @deprecated Moved to `userDevices/{uid}`, which is not world-readable and
     * holds more than one device. Still read as a fallback until the migration
     * has run everywhere; see scripts/migrate-fcm-tokens.ts.
     */
    fcmToken: z.string().optional(),
  })
  .catch({
    notificationsEnabled: false,
    friendNotificationsEnabled: false,
    crownNotificationsEnabled: false,
    reminderMinuteOfDay: DEFAULT_REMINDER_MINUTE,
    morningNotificationEnabled: false,
    morningNotificationMinuteOfDay: DEFAULT_MORNING_NOTIFICATION_MINUTE,
  });

export const userSchema = z.object({
  id: z.string(),
  /** Duplicated at the top level for backward compatibility with old documents. */
  displayName: z.string().optional(),
  /** One-way follows, not mutual friendships, despite the UI wording. */
  friends: z.array(z.string()).catch([]),
  settings: userSettingsSchema,
});

export type UserSettings = z.infer<typeof userSettingsSchema>;
export type User = z.infer<typeof userSchema>;

/** The name to show for a user, preferring the newer settings field. */
export function displayNameOf(user: Pick<User, 'displayName' | 'settings'>): string {
  return user.settings?.displayName || user.displayName || 'Anonymous';
}

/**
 * Push tokens, one document per user, one entry per device.
 *
 * Deliberately not on the public profile. `users` is readable by any signed-in
 * player, so a token stored there was handed to everyone. Rules make this
 * write-only by its owner and readable by nobody; functions reach it through
 * the Admin SDK, which bypasses rules.
 */
export const userDevicesSchema = z.object({
  tokens: z.array(z.string()).catch([]),
});

export type UserDevices = z.infer<typeof userDevicesSchema>;

export const userStatsSchema = z.object({
  crownCount: z.number().catch(0),
});

export type UserStats = z.infer<typeof userStatsSchema>;

/* -------------------------------------------------------------------------- */
/* Daily prompt                                                                */
/* -------------------------------------------------------------------------- */

export const aiGenerationStatusSchema = z.enum(['success', 'failed', 'pending']);
export type AiGenerationStatus = z.infer<typeof aiGenerationStatusSchema>;

/** The single `dailyCategories/current` document — the whole game state for today. */
export const dailyPromptSchema = z.object({
  category: z.string().catch(''),
  output: z.string().catch(''),
  categoryIndex: z.number().catch(0),
  outputIndex: z.number().catch(0),
  aiGeneratedPun: z.string().optional(),
  aiGenerationStatus: aiGenerationStatusSchema.optional(),
});

export type DailyPrompt = z.infer<typeof dailyPromptSchema>;

/* -------------------------------------------------------------------------- */
/* Parsing helpers                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Parse one Firestore document, returning null instead of throwing when the
 * data cannot be salvaged. Callers filter nulls out, so a single corrupt
 * document degrades to one missing row rather than an empty screen.
 */
export function parseDoc<T>(
  schema: z.ZodType<T>,
  id: string,
  data: unknown,
  context: string,
): T | null {
  const result = schema.safeParse({ ...(data as object), id });

  if (!result.success) {
    console.error(`[${context}] Skipping malformed document ${id}:`, result.error.issues);
    return null;
  }

  return result.data;
}
